import React, { useState, useRef } from "react";
import { useModal } from "../../context/ModalContext";

const CHECK_ICON = (
    <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" viewBox="0 0 24 24">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

const STEPS = [
    {
        n: "01",
        t: "Discover",
        s: "Map your process & goals",
        heading: "First, we learn exactly how your business runs.",
        lead: "Before a line of code, we map your workflows, data and goals — so what we build solves the real problem, not a generic one.",
        checks: ["Workflow & process mapping", "Stakeholder interviews", "Goals & success metrics", "Technical discovery"],
    },
    {
        n: "02",
        t: "Design",
        s: "Prototype the experience",
        heading: "Then we shape the experience before building it.",
        lead: "We prototype the screens and flows so you can see, test and refine the solution while it is still quick and cheap to change.",
        checks: ["Interactive prototypes", "UX & interface design", "Design system & components", "Early feedback loops"],
    },
    {
        n: "03",
        t: "Build",
        s: "Engineer, test & ship",
        heading: "When off-the-shelf can't keep up, we build the thing that does.",
        lead: "Every business runs a little differently — and the software that runs it should reflect that. We architect custom systems that remove the manual work, connect the tools you already use, and grow without hitting a wall.",
        checks: ["Tailored web platforms & portals", "Workflow & process automation", "Third-party & API integrations", "Secure, scalable architecture"],
    },
    {
        n: "04",
        t: "Scale",
        s: "Support & grow with you",
        heading: "After launch, we help it grow with you.",
        lead: "We monitor, refine and extend the system as your needs change — so the software keeps pace with the business instead of holding it back.",
        checks: ["Proactive monitoring", "Performance & security", "New features on demand", "Dedicated support"],
    },
];

export default function Spotlight() {
    const [active, setActive] = useState(0);
    const { openModal } = useModal();
    const contentRef = useRef(null);

    const handleTabClick = (i) => {
        setActive(i);

        // Only auto-scroll on small screens (mobile)
        try {
            const isMobile = typeof window !== 'undefined' && (window.innerWidth || 0) <= 768;
            if (!isMobile) return;

            // Allow DOM updates, then measure & scroll. Use a small delay and fallbacks for reliability.
            setTimeout(() => {
                const attempt = () => {
                    const el = contentRef.current;
                    if (!el) return;
                    const header = document.querySelector('header');
                    const headerHeight = header ? header.offsetHeight : 0;
                    const top = el.getBoundingClientRect().top + window.pageYOffset - headerHeight - 12;

                    try {
                        window.scrollTo({ top, behavior: 'smooth' });
                    } catch (err) {
                        try {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            if (headerHeight) window.scrollBy(0, -headerHeight - 12);
                        } catch (e) {
                            // final fallback: instant scroll
                            window.scrollTo(0, top);
                        }
                    }
                };

                // Try immediately, then once more on next frame to ensure layout settled
                attempt();
                requestAnimationFrame(() => requestAnimationFrame(attempt));
            }, 120);
        } catch (e) {
            // silently ignore failures
        }
    };

    return (
        <section className="container spotlight">
            <div className="build-card">
                <div className="label">HOW WE BUILD</div>
                <div className="build-steps" role="tablist" aria-label="How we build">
                    {STEPS.map((step, i) => (
                        <button
                            key={step.n}
                            type="button"
                            role="tab"
                            aria-selected={active === i}
                            className={`build-step${active === i ? " active" : ""}`}
                            data-tab={i + 1}
                            onClick={() => handleTabClick(i)}
                        >
                            <span className="n">{step.n}</span>
                            <div>
                                <div className="t">{step.t}</div>
                                <div className="s">{step.s}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="spotlight-content" ref={contentRef}>
                <div className="eyebrow">{STEPS[active].t}</div>
                <div className="spot-panels">
                    {STEPS.map((step, i) => (
                        <div key={step.n} className={`spot-panel${active === i ? " active" : ""}`} data-panel={i + 1}>
                            <h2 className="h-lg">{step.heading}</h2>
                            <p className="lead">{step.lead}</p>
                            <div className="checks">
                                {step.checks.map((c) => (
                                    <div className="check" key={c}>
                                        <span className="tick">{CHECK_ICON}</span>
                                        <span>{c}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <button className="btn btn-dark" onClick={openModal} type="button">Discuss your project</button>
            </div>
        </section>
    );
}
